import { Alert, Button, Typography } from 'antd';
import { Alert01Icon, RefreshIcon } from '@hugeicons/core-free-icons';
import { ApiError, messageFor, NetworkError } from '@/lib/api/index.js';
import { Icon } from '../Icon/Icon.js';
import { space, typography } from '@/design/tokens.js';

const { Title, Text } = Typography;

export interface ErrorStateProps {
  error: unknown;
  /** Shown as a retry button when provided. */
  onRetry?: () => void;
  /** Compact inline form, for a failed panel inside an otherwise working page. */
  inline?: boolean;
}

/**
 * Shown when something failed.
 *
 * Three things every error the user sees must have, and this guarantees all
 * three:
 *
 *   1. Plain language about what went wrong.
 *   2. A way forward — retry, or a note that the team has been told.
 *   3. The request id, for a support conversation. Shown quietly, but shown.
 *
 * The id is the difference between "it broke" and a ticket an engineer can
 * resolve in one log search.
 */
export function ErrorState({ error, onRetry, inline = false }: ErrorStateProps) {
  const message = messageFor(error);
  const requestId = ApiError.is(error) ? error.requestId : null;
  const offline = error instanceof NetworkError;

  if (inline) {
    return (
      <Alert
        type="error"
        showIcon
        title={message}
        description={requestId ? `Reference: ${requestId}` : undefined}
        action={
          onRetry ? (
            <Button size="small" onClick={onRetry} icon={<Icon icon={RefreshIcon} size="xs" />}>
              Retry
            </Button>
          ) : undefined
        }
      />
    );
  }

  return (
    <div
      role="alert"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        padding: `${space[16]}px ${space[6]}px`,
        gap: space[2],
      }}
    >
      <span
        aria-hidden
        style={{
          display: 'grid',
          placeItems: 'center',
          width: 52,
          height: 52,
          borderRadius: 12,
          backgroundColor: 'var(--ant-color-error-bg)',
          color: 'var(--ant-color-error)',
          marginBottom: space[2],
        }}
      >
        <Icon icon={Alert01Icon} size="2xl" />
      </span>

      <Title level={3} style={{ margin: 0, fontSize: typography.size.lg }}>
        {offline ? 'No connection' : 'Something went wrong'}
      </Title>

      <Text type="secondary" style={{ maxWidth: 440 }}>
        {message}
      </Text>

      {onRetry ? (
        <Button
          type="primary"
          onClick={onRetry}
          icon={<Icon icon={RefreshIcon} size="sm" />}
          style={{ marginTop: space[3] }}
        >
          Try again
        </Button>
      ) : null}

      {requestId ? (
        <Text
          type="secondary"
          copyable={{ text: requestId }}
          style={{ fontSize: typography.size.xs, marginTop: space[3] }}
        >
          Reference: {requestId}
        </Text>
      ) : null}
    </div>
  );
}
